import React, { useEffect, useMemo, useState } from 'react';
import { portalApi } from '../../../../lib/portalApi';
import { QUESTION_TYPES } from '../../shared/DynamicFormFields';
import {
  GripVertical,
  ChevronUp,
  ChevronDown,
  Edit3,
  Trash2,
  Filter,
  Search,
  X,
  Layers,
  FileText,
  Package,
} from 'lucide-react';

/**
 * Admin form bank builder for apply | profile | dd.
 * Tabs: Stages · Questions · Documents · (Packs for DD)
 */
export function AdminFormBuilderPage({ kind }) {
  const [bank, setBank] = useState(null);
  const [tab, setTab] = useState('stages');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [stageForm, setStageForm] = useState({ title: '', subtitle: '' });
  const [qForm, setQForm] = useState({
    label: '',
    helpText: '',
    type: 'text',
    stageId: '',
    required: false,
    active: true,
    choices: '',
  });
  const [docForm, setDocForm] = useState({
    label: '',
    description: '',
    stageId: '',
    required: false,
    active: true,
    accept: '.pdf,.png,.jpg,.jpeg,.webp',
  });
  const [packForm, setPackForm] = useState({ name: '', description: '' });
  const [qSearch, setQSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('ALL');
  const [docStageFilter, setDocStageFilter] = useState('ALL');
  const [editingQuestion, setEditingQuestion] = useState(null);

  const load = () => {
    const fn =
      kind === 'apply'
        ? portalApi.getApplyBank
        : kind === 'profile'
          ? portalApi.companyProfileBank
          : portalApi.ddForm;
    return fn()
      .then(setBank)
      .catch((e) => {
        setError(e.message);
        setBank(null);
      });
  };

  useEffect(() => {
    setError('');
    load();
  }, [kind]);

  const title =
    kind === 'apply' ? 'InnovAce apply form' : kind === 'profile' ? 'Company profile' : 'Due Diligence';

  const stages = bank?.stages || [];

  // Master deduplicated list of questions preserving sort order
  const allQuestions = useMemo(() => {
    const all = bank?.questions || stages.flatMap((s) => s.questions || []) || [];
    const uniq = [];
    const seen = new Set();
    for (const q of all) {
      if (seen.has(q.id)) continue;
      seen.add(q.id);
      uniq.push(q);
    }
    return uniq;
  }, [bank, stages]);

  // Questions filtered by stage and search
  const filteredQuestions = useMemo(() => {
    let list = allQuestions;
    if (stageFilter === 'UNASSIGNED') {
      list = list.filter((q) => !q.stageId);
    } else if (stageFilter !== 'ALL') {
      list = list.filter((q) => String(q.stageId) === String(stageFilter));
    }
    if (qSearch.trim()) {
      const s = qSearch.toLowerCase();
      list = list.filter((q) => q.label?.toLowerCase().includes(s) || q.helpText?.toLowerCase().includes(s));
    }
    return list;
  }, [allQuestions, stageFilter, qSearch]);

  const docs = bank?.documentRequirements || stages.flatMap((s) => s.documentRequirements || []);
  const filteredDocs = useMemo(() => {
    if (docStageFilter === 'UNASSIGNED') {
      return docs.filter((d) => !d.stageId);
    } else if (docStageFilter !== 'ALL') {
      return docs.filter((d) => String(d.stageId) === String(docStageFilter));
    }
    return docs;
  }, [docs, docStageFilter]);

  const run = async (fn) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const createStage = (e) => {
    e.preventDefault();
    if (!stageForm.title.trim()) return;
    run(async () => {
      const body = { title: stageForm.title.trim(), subtitle: stageForm.subtitle || '' };
      if (kind === 'apply') await portalApi.createApplyStage(body);
      else if (kind === 'profile') await portalApi.createProfileStage(body);
      else await portalApi.createDdStage(body);
      setStageForm({ title: '', subtitle: '' });
    });
  };

  const createQuestion = (e) => {
    e.preventDefault();
    if (!qForm.label.trim()) return;
    run(async () => {
      const choices = qForm.choices
        .split('\n')
        .map((x) => x.trim())
        .filter(Boolean);
      const body = {
        label: qForm.label.trim(),
        helpText: qForm.helpText || '',
        type: qForm.type,
        stageId: qForm.stageId || undefined,
        required: !!qForm.required,
        active: qForm.active !== false,
        options: ['select', 'multiselect'].includes(qForm.type) ? { choices } : { choices: [] },
      };
      if (kind === 'apply') await portalApi.createApplyQuestion(body);
      else if (kind === 'profile') await portalApi.createProfileQuestion(body);
      else await portalApi.createDdQuestion(body);
      setQForm({
        label: '',
        helpText: '',
        type: 'text',
        stageId: qForm.stageId,
        required: false,
        active: true,
        choices: '',
      });
    });
  };

  const handleUpdateQuestion = (e) => {
    e.preventDefault();
    if (!editingQuestion || !editingQuestion.label.trim()) return;
    run(async () => {
      const choices = (editingQuestion.choices || '')
        .split('\n')
        .map((x) => x.trim())
        .filter(Boolean);
      const body = {
        label: editingQuestion.label.trim(),
        helpText: editingQuestion.helpText || '',
        type: editingQuestion.type,
        stageId: editingQuestion.stageId || null,
        required: !!editingQuestion.required,
        active: editingQuestion.active !== false,
        options: ['select', 'multiselect'].includes(editingQuestion.type) ? { choices } : { choices: [] },
      };
      if (kind === 'apply') await portalApi.updateApplyQuestion(editingQuestion.id, body);
      else if (kind === 'profile') await portalApi.updateProfileQuestion(editingQuestion.id, body);
      else await portalApi.updateDdQuestion(editingQuestion.id, body);
      setEditingQuestion(null);
    });
  };

  const handleMoveQuestion = (questionId, direction) => {
    const list = [...allQuestions];
    const idx = list.findIndex((q) => q.id === questionId);
    if (idx === -1) return;
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= list.length) return;

    const temp = list[idx];
    list[idx] = list[targetIdx];
    list[targetIdx] = temp;

    const ids = list.map((q) => q.id);
    run(async () => {
      if (kind === 'apply') await portalApi.reorderApplyQuestions(ids);
      else if (kind === 'profile') await portalApi.reorderProfileQuestions(ids);
      else await portalApi.reorderDdQuestions(ids);
    });
  };

  const handleDragStart = (e, qId) => {
    e.dataTransfer.setData('text/plain', qId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = (e, targetQId) => {
    e.preventDefault();
    const sourceQId = e.dataTransfer.getData('text/plain');
    if (!sourceQId || sourceQId === targetQId) return;

    const list = [...allQuestions];
    const fromIdx = list.findIndex((q) => q.id === sourceQId);
    const toIdx = list.findIndex((q) => q.id === targetQId);
    if (fromIdx === -1 || toIdx === -1) return;

    const [moved] = list.splice(fromIdx, 1);
    list.splice(toIdx, 0, moved);

    const ids = list.map((q) => q.id);
    run(async () => {
      if (kind === 'apply') await portalApi.reorderApplyQuestions(ids);
      else if (kind === 'profile') await portalApi.reorderProfileQuestions(ids);
      else await portalApi.reorderDdQuestions(ids);
    });
  };

  const createDoc = (e) => {
    e.preventDefault();
    if (!docForm.label.trim()) return;
    run(async () => {
      const body = {
        label: docForm.label.trim(),
        description: docForm.description || '',
        stageId: docForm.stageId || undefined,
        required: !!docForm.required,
        active: docForm.active !== false,
        accept: docForm.accept || '.pdf,.png,.jpg,.jpeg,.webp',
      };
      if (kind === 'apply') await portalApi.createApplyDocReq(body);
      else if (kind === 'profile') await portalApi.createProfileDocReq(body);
      else await portalApi.createDdDocReq(body);
      setDocForm({
        label: '',
        description: '',
        stageId: docForm.stageId,
        required: false,
        active: true,
        accept: '.pdf,.png,.jpg,.jpeg,.webp',
      });
    });
  };

  const createPack = (e) => {
    e.preventDefault();
    if (!packForm.name.trim()) return;
    run(async () => {
      await portalApi.createDdPack({
        name: packForm.name.trim(),
        description: packForm.description || '',
        questionIds: allQuestions.map((q) => q.id),
        documentIds: docs.map((d) => d.id),
      });
      setPackForm({ name: '', description: '' });
    });
  };

  const deleteQuestion = (id) =>
    run(async () => {
      if (!window.confirm('Delete this question?')) return;
      if (kind === 'apply') await portalApi.deleteApplyQuestion(id);
      else if (kind === 'profile') await portalApi.deleteProfileQuestion(id);
      else await portalApi.deleteDdQuestion(id);
    });

  const deleteStage = (id) =>
    run(async () => {
      if (!window.confirm('Delete this stage?')) return;
      if (kind === 'apply') await portalApi.deleteApplyStage(id);
      else if (kind === 'profile') await portalApi.deleteProfileStage(id);
      else await portalApi.deleteDdStage(id);
    });

  const deleteDoc = (id) =>
    run(async () => {
      if (!window.confirm('Delete this document requirement?')) return;
      if (kind === 'apply') await portalApi.deleteApplyDocReq(id);
      else if (kind === 'profile') await portalApi.deleteProfileDocReq(id);
      else await portalApi.deleteDdDocReq(id);
    });

  const tabs = [
    { id: 'stages', label: `Stages ${stages.length}` },
    { id: 'questions', label: `Questions ${allQuestions.length}` },
    { id: 'documents', label: `Documents ${docs.length}` },
  ];
  if (kind === 'dd') tabs.push({ id: 'packs', label: `Packs ${(bank?.packs || []).length}` });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-white">{title} builder</h1>
        <p className="text-sm text-white/45 mt-1">
          Stages, questions (all types including file → Cloudinary), and document uploads.
          {kind === 'apply' &&
            ' Active fields appear on the public InnovAce /apply page. Submissions become Applications; when you Accept, the founder and startup enter onboarding.'}
          {kind === 'profile' && ' Founders fill this from Company Profile.'}
          {kind === 'dd' && ' Open DD on a startup to collect answers.'}
        </p>
      </div>

      {error && <p className="text-red-400 text-sm">{error}</p>}

      <div className="flex flex-wrap items-center gap-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
              tab === t.id
                ? 'border-[#d42b92] bg-[#d42b92]/20 text-white shadow-sm shadow-[#d42b92]/20'
                : 'border-white/10 text-white/50 hover:text-white/80 hover:border-white/20'
            }`}
          >
            {t.label}
          </button>
        ))}

        {tab === 'questions' && (
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 bg-black/40 border border-white/10 rounded-xl px-2.5 py-1.5 text-xs text-white">
              <Filter className="w-3.5 h-3.5 text-white/40" />
              <select
                value={stageFilter}
                onChange={(e) => setStageFilter(e.target.value)}
                className="bg-transparent text-white text-xs focus:outline-none cursor-pointer"
                title="Filter questions by stage"
              >
                <option value="ALL" className="bg-[#121216] text-white">
                  All Stages ({allQuestions.length})
                </option>
                {stages.map((s, idx) => {
                  const count = allQuestions.filter((q) => q.stageId === s.id).length;
                  return (
                    <option key={s.id} value={s.id} className="bg-[#121216] text-white">
                      Stage {idx + 1}: {s.title} ({count})
                    </option>
                  );
                })}
                <option value="UNASSIGNED" className="bg-[#121216] text-white">
                  Unassigned ({allQuestions.filter((q) => !q.stageId).length})
                </option>
              </select>
            </div>
            <div className="relative">
              <input
                value={qSearch}
                onChange={(e) => setQSearch(e.target.value)}
                placeholder="Search questions…"
                className="bg-black/40 border border-white/10 rounded-xl px-3 py-1.5 text-white text-sm min-w-[180px] focus:outline-none focus:border-[#d42b92]/60"
              />
              {qSearch && (
                <button
                  type="button"
                  onClick={() => setQSearch('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        )}

        {tab === 'documents' && (
          <div className="ml-auto flex items-center gap-1.5 bg-black/40 border border-white/10 rounded-xl px-2.5 py-1.5 text-xs text-white">
            <Filter className="w-3.5 h-3.5 text-white/40" />
            <select
              value={docStageFilter}
              onChange={(e) => setDocStageFilter(e.target.value)}
              className="bg-transparent text-white text-xs focus:outline-none cursor-pointer"
              title="Filter documents by stage"
            >
              <option value="ALL" className="bg-[#121216] text-white">
                All Stages ({docs.length})
              </option>
              {stages.map((s, idx) => {
                const count = docs.filter((d) => d.stageId === s.id).length;
                return (
                  <option key={s.id} value={s.id} className="bg-[#121216] text-white">
                    Stage {idx + 1}: {s.title} ({count})
                  </option>
                );
              })}
              <option value="UNASSIGNED" className="bg-[#121216] text-white">
                Unassigned ({docs.filter((d) => !d.stageId).length})
              </option>
            </select>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-3 min-h-[240px]">
          {tab === 'stages' && (
            <>
              {stages.length === 0 && (
                <div className="rounded-2xl border border-dashed border-white/15 p-10 text-center text-white/40 text-sm">
                  No stages yet. Add a stage to group questions.
                </div>
              )}
              {stages.map((s, idx) => (
                <div key={s.id} className="rounded-2xl border border-white/10 bg-white/[0.015] p-4">
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/60 font-mono">
                          Stage {idx + 1}
                        </span>
                        <span className="text-white font-medium">{s.title}</span>
                      </div>
                      {s.subtitle && <div className="text-xs text-white/40 mt-1">{s.subtitle}</div>}
                      <div className="text-xs text-white/35 mt-2">
                        {(s.questions || allQuestions.filter((q) => q.stageId === s.id)).length} questions ·{' '}
                        {(s.documentRequirements || docs.filter((d) => d.stageId === s.id)).length} docs
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => deleteStage(s.id)}
                      className="text-xs text-red-400/80 hover:text-red-300 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}

          {tab === 'questions' && (
            <>
              {filteredQuestions.length === 0 && (
                <div className="rounded-2xl border border-dashed border-white/15 p-10 text-center text-white/40 text-sm">
                  {qSearch || stageFilter !== 'ALL'
                    ? 'No questions match the selected filter or search.'
                    : 'No questions found. Add your first question from the right panel.'}
                </div>
              )}
              {filteredQuestions.map((q) => {
                const globalIdx = allQuestions.findIndex((item) => item.id === q.id);
                const stage = stages.find((s) => s.id === q.stageId);
                const isFirst = globalIdx === 0;
                const isLast = globalIdx === allQuestions.length - 1;

                return (
                  <div
                    key={q.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, q.id)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => handleDrop(e, q.id)}
                    className="rounded-xl border border-white/10 bg-white/[0.015] hover:border-white/20 px-3.5 py-3 flex items-center justify-between gap-3 group transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {/* Reorder grip & buttons */}
                      <div className="flex items-center gap-1 text-white/30 group-hover:text-white/60 shrink-0">
                        <GripVertical className="w-4 h-4 cursor-grab active:cursor-grabbing hover:text-[#d42b92]" />
                        <div className="flex flex-col">
                          <button
                            type="button"
                            disabled={busy || isFirst}
                            onClick={() => handleMoveQuestion(q.id, 'up')}
                            className="p-0.5 hover:text-white disabled:opacity-20 disabled:hover:text-white/30"
                            title="Move Up"
                          >
                            <ChevronUp className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            disabled={busy || isLast}
                            onClick={() => handleMoveQuestion(q.id, 'down')}
                            className="p-0.5 hover:text-white disabled:opacity-20 disabled:hover:text-white/30"
                            title="Move Down"
                          >
                            <ChevronDown className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>

                      {/* Question details */}
                      <div className="min-w-0">
                        <div className="text-white text-sm font-medium truncate">{q.label}</div>
                        {q.helpText && (
                          <div className="text-xs text-white/40 truncate max-w-md">{q.helpText}</div>
                        )}
                        <div className="text-[11px] text-white/40 mt-0.5 flex items-center gap-2 flex-wrap">
                          <span className="px-1.5 py-0.2 rounded bg-white/5 font-mono text-[10px] text-white/60">
                            {q.type}
                          </span>
                          {q.required && <span className="text-[#ff8ec5]">required</span>}
                          <span>·</span>
                          <span>{stage ? stage.title : 'Unassigned'}</span>
                          {!q.active && <span className="text-amber-400/80">(inactive)</span>}
                        </div>
                      </div>
                    </div>

                    {/* Actions: Edit & Delete */}
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() =>
                          setEditingQuestion({
                            id: q.id,
                            label: q.label || '',
                            helpText: q.helpText || '',
                            type: q.type || 'text',
                            stageId: q.stageId || '',
                            required: !!q.required,
                            active: q.active !== false,
                            choices: (q.options?.choices || []).join('\n'),
                          })
                        }
                        className="p-1.5 rounded-lg border border-white/10 hover:border-[#d42b92]/50 hover:bg-[#d42b92]/10 text-white/60 hover:text-white transition-colors"
                        title="Edit question"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteQuestion(q.id)}
                        className="p-1.5 rounded-lg border border-white/10 hover:border-red-500/50 hover:bg-red-500/10 text-red-400/80 hover:text-red-300 transition-colors"
                        title="Delete question"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {tab === 'documents' && (
            <>
              {filteredDocs.length === 0 && (
                <div className="rounded-2xl border border-dashed border-white/15 p-10 text-center text-white/40 text-sm">
                  {docStageFilter !== 'ALL'
                    ? 'No document requirements found for this stage.'
                    : 'No document requirements. Add uploads (stored on Cloudinary).'}
                </div>
              )}
              {filteredDocs.map((d) => {
                const stage = stages.find((s) => s.id === d.stageId);
                return (
                  <div
                    key={d.id}
                    className="rounded-xl border border-white/10 bg-white/[0.015] px-4 py-3 flex justify-between items-center gap-3"
                  >
                    <div>
                      <div className="text-white text-sm font-medium">{d.label}</div>
                      {d.description && <div className="text-xs text-white/40">{d.description}</div>}
                      <div className="text-[11px] text-white/40 mt-1">
                        {d.required ? 'required · ' : ''}
                        {d.accept || 'any files'}
                        {stage ? ` · ${stage.title}` : ' · Unassigned'}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => deleteDoc(d.id)}
                      className="p-1.5 rounded-lg border border-white/10 hover:border-red-500/50 hover:bg-red-500/10 text-red-400/80 hover:text-red-300 transition-colors"
                      title="Delete document requirement"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </>
          )}

          {tab === 'packs' && (
            <>
              {(bank?.packs || []).map((p) => (
                <div
                  key={p.id}
                  className="rounded-xl border border-white/10 bg-white/[0.015] px-4 py-3 text-sm text-white/70"
                >
                  <div className="text-white font-medium">{p.name}</div>
                  {p.description && <div className="text-xs text-white/40 mt-0.5">{p.description}</div>}
                  <div className="text-xs text-white/40 mt-1.5">
                    {(p.questionIds || []).length} questions · {(p.documentIds || []).length} docs
                  </div>
                </div>
              ))}
              {(bank?.packs || []).length === 0 && (
                <p className="text-white/40 text-sm">No packs yet. Create one from all active fields.</p>
              )}
            </>
          )}
        </div>

        <aside className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 h-fit space-y-3">
          {tab === 'stages' && (
            <form onSubmit={createStage} className="space-y-3">
              <div className="text-white font-medium">Add stage</div>
              <input
                value={stageForm.title}
                onChange={(e) => setStageForm({ ...stageForm, title: e.target.value })}
                placeholder="Stage title"
                className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d42b92]/60"
              />
              <input
                value={stageForm.subtitle}
                onChange={(e) => setStageForm({ ...stageForm, subtitle: e.target.value })}
                placeholder="Subtitle (optional)"
                className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d42b92]/60"
              />
              <button
                disabled={busy}
                className="w-full px-3 py-2 rounded-xl bg-[#d42b92] hover:bg-[#d42b92]/90 text-white text-sm font-medium transition-colors"
              >
                + Add stage
              </button>
            </form>
          )}

          {tab === 'questions' && (
            <form onSubmit={createQuestion} className="space-y-3">
              <div className="text-white font-medium">Add question</div>
              <label className="block text-[10px] uppercase tracking-wide text-white/40">Stage / page</label>
              <select
                value={qForm.stageId}
                onChange={(e) => setQForm({ ...qForm, stageId: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d42b92]/60"
              >
                <option value="">Unassigned</option>
                {stages.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.title}
                  </option>
                ))}
              </select>
              <input
                value={qForm.label}
                onChange={(e) => setQForm({ ...qForm, label: e.target.value })}
                placeholder="Question label"
                required
                className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d42b92]/60"
              />
              <textarea
                value={qForm.helpText}
                onChange={(e) => setQForm({ ...qForm, helpText: e.target.value })}
                placeholder="Help text"
                rows={2}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d42b92]/60"
              />
              <label className="block text-[10px] uppercase tracking-wide text-white/40">Question type</label>
              <select
                value={qForm.type}
                onChange={(e) => setQForm({ ...qForm, type: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d42b92]/60"
              >
                {QUESTION_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              {['select', 'multiselect'].includes(qForm.type) && (
                <textarea
                  value={qForm.choices}
                  onChange={(e) => setQForm({ ...qForm, choices: e.target.value })}
                  placeholder="One option per line"
                  rows={3}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d42b92]/60"
                />
              )}
              <label className="flex items-center gap-2 text-sm text-white/70">
                <input
                  type="checkbox"
                  checked={qForm.required}
                  onChange={(e) => setQForm({ ...qForm, required: e.target.checked })}
                />
                Required
              </label>
              <label className="flex items-center gap-2 text-sm text-white/70">
                <input
                  type="checkbox"
                  checked={qForm.active}
                  onChange={(e) => setQForm({ ...qForm, active: e.target.checked })}
                />
                Active
              </label>
              <button
                disabled={busy}
                className="w-full px-3 py-2 rounded-xl bg-[#d42b92] hover:bg-[#d42b92]/90 text-white text-sm font-medium transition-colors"
              >
                + Add question
              </button>
            </form>
          )}

          {tab === 'documents' && (
            <form onSubmit={createDoc} className="space-y-3">
              <div className="text-white font-medium">Add document</div>
              <select
                value={docForm.stageId}
                onChange={(e) => setDocForm({ ...docForm, stageId: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d42b92]/60"
              >
                <option value="">Unassigned</option>
                {stages.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.title}
                  </option>
                ))}
              </select>
              <input
                value={docForm.label}
                onChange={(e) => setDocForm({ ...docForm, label: e.target.value })}
                placeholder="Document label"
                required
                className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d42b92]/60"
              />
              <textarea
                value={docForm.description}
                onChange={(e) => setDocForm({ ...docForm, description: e.target.value })}
                placeholder="Description"
                rows={2}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d42b92]/60"
              />
              <input
                value={docForm.accept}
                onChange={(e) => setDocForm({ ...docForm, accept: e.target.value })}
                placeholder="Accept (.pdf,.png…)"
                className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d42b92]/60"
              />
              <label className="flex items-center gap-2 text-sm text-white/70">
                <input
                  type="checkbox"
                  checked={docForm.required}
                  onChange={(e) => setDocForm({ ...docForm, required: e.target.checked })}
                />
                Required
              </label>
              <button
                disabled={busy}
                className="w-full px-3 py-2 rounded-xl bg-[#d42b92] hover:bg-[#d42b92]/90 text-white text-sm font-medium transition-colors"
              >
                + Add document
              </button>
            </form>
          )}

          {tab === 'packs' && (
            <form onSubmit={createPack} className="space-y-3">
              <div className="text-white font-medium">Add pack</div>
              <p className="text-xs text-white/40">
                Pack includes all current active questions and documents.
              </p>
              <input
                value={packForm.name}
                onChange={(e) => setPackForm({ ...packForm, name: e.target.value })}
                placeholder="Pack name"
                className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d42b92]/60"
              />
              <button
                disabled={busy}
                className="w-full px-3 py-2 rounded-xl bg-[#d42b92] hover:bg-[#d42b92]/90 text-white text-sm font-medium transition-colors"
              >
                + Add pack
              </button>
            </form>
          )}
        </aside>
      </div>

      {/* Edit Question Modal */}
      {editingQuestion && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0f0f12] border border-white/15 rounded-2xl w-full max-w-lg p-6 space-y-4 shadow-2xl relative">
            <div className="flex items-center justify-between pb-3 border-b border-white/10">
              <div className="flex items-center gap-2">
                <Edit3 className="w-4 h-4 text-[#d42b92]" />
                <h3 className="text-lg font-semibold text-white">Edit Question</h3>
              </div>
              <button
                type="button"
                onClick={() => setEditingQuestion(null)}
                className="text-white/40 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleUpdateQuestion} className="space-y-3.5">
              <div>
                <label className="block text-[11px] uppercase tracking-wide text-white/50 mb-1">
                  Question Label *
                </label>
                <input
                  value={editingQuestion.label}
                  onChange={(e) => setEditingQuestion({ ...editingQuestion, label: e.target.value })}
                  required
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d42b92]"
                />
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-wide text-white/50 mb-1">
                  Help Text
                </label>
                <textarea
                  value={editingQuestion.helpText}
                  onChange={(e) => setEditingQuestion({ ...editingQuestion, helpText: e.target.value })}
                  rows={2}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d42b92]"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] uppercase tracking-wide text-white/50 mb-1">
                    Stage / Page
                  </label>
                  <select
                    value={editingQuestion.stageId}
                    onChange={(e) => setEditingQuestion({ ...editingQuestion, stageId: e.target.value })}
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d42b92]"
                  >
                    <option value="">Unassigned</option>
                    {stages.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.title}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] uppercase tracking-wide text-white/50 mb-1">
                    Question Type
                  </label>
                  <select
                    value={editingQuestion.type}
                    onChange={(e) => setEditingQuestion({ ...editingQuestion, type: e.target.value })}
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d42b92]"
                  >
                    {QUESTION_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {['select', 'multiselect'].includes(editingQuestion.type) && (
                <div>
                  <label className="block text-[11px] uppercase tracking-wide text-white/50 mb-1">
                    Choices (One per line)
                  </label>
                  <textarea
                    value={editingQuestion.choices}
                    onChange={(e) => setEditingQuestion({ ...editingQuestion, choices: e.target.value })}
                    rows={3}
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d42b92]"
                  />
                </div>
              )}

              <div className="flex items-center gap-6 pt-1">
                <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingQuestion.required}
                    onChange={(e) =>
                      setEditingQuestion({ ...editingQuestion, required: e.target.checked })
                    }
                    className="accent-[#d42b92]"
                  />
                  Required
                </label>

                <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingQuestion.active}
                    onChange={(e) =>
                      setEditingQuestion({ ...editingQuestion, active: e.target.checked })
                    }
                    className="accent-[#d42b92]"
                  />
                  Active
                </label>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setEditingQuestion(null)}
                  className="px-4 py-2 rounded-xl border border-white/10 text-white/70 hover:text-white hover:bg-white/5 text-sm transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={busy}
                  className="px-5 py-2 rounded-xl bg-[#d42b92] hover:bg-[#d42b92]/90 text-white font-medium text-sm transition-colors shadow-lg shadow-[#d42b92]/25"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminFormBuilderPage;
